import React, { useState } from 'react';
import {
  Share2,
  Users,
  ShieldAlert,
  CheckCircle2,
  XCircle,
  Copy,
  Gift,
  Award,
  Link,
  ShieldCheck,
  AlertTriangle
} from 'lucide-react';
import { INITIAL_REFERRAL_TELEMETRY } from '../../data/growthData';
import { ReferralProgramTelemetry } from '../../types';

export const ReferralProgramHub: React.FC = () => {
  const [telemetry] = useState<ReferralProgramTelemetry>(INITIAL_REFERRAL_TELEMETRY);
  const [copiedLink, setCopiedLink] = useState<boolean>(false);
  const sampleRefLink = 'https://yamoopay.com/r/YM-CALC-8492';

  const copyLinkToClipboard = () => {
    navigator.clipboard.writeText(sampleRefLink);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  return (
    <div className="space-y-6">
      {/* Header Banner */}
      <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-cyan-100 text-cyan-900">
              P12.5 — Referral & Peer Advocacy Engine
            </span>
            <span className="flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
              Anti-Abuse Fraud Engine Active
            </span>
          </div>
          <h2 className="text-xl font-black text-slate-900 tracking-tight">
            Peer Advocacy Referral Program & Fraud Prevention Engine
          </h2>
          <p className="text-xs text-slate-600 mt-1 max-w-3xl">
            Rewards authentic peer recommendations with non-cash feature unlocks. Blocks self-referrals, disposable email farms, and rapid automated bot abuse.
          </p>
        </div>

        <div className="bg-cyan-50 px-3.5 py-2 rounded-lg border border-cyan-200 text-right">
          <span className="text-[10px] text-cyan-800 font-bold block uppercase">Reward Model</span>
          <span className="text-xs font-black text-cyan-950">NON-CASH FEATURE UNLOCKS</span>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs">
          <span className="text-slate-500 text-[11px] block font-medium">Active Referrers</span>
          <span className="text-lg font-black text-slate-900">{telemetry.activeReferrersCount.toLocaleString()}</span>
        </div>
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs">
          <span className="text-slate-500 text-[11px] block font-medium">Invites Sent</span>
          <span className="text-lg font-black text-slate-900">{telemetry.totalInvitesSentCount.toLocaleString()}</span>
        </div>
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs">
          <span className="text-slate-500 text-[11px] block font-medium">Qualified Referrals</span>
          <span className="text-lg font-black text-emerald-700">{telemetry.totalQualifiedReferralsCount.toLocaleString()}</span>
        </div>
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs">
          <span className="text-slate-500 text-[11px] block font-medium">Conversion Rate</span>
          <span className="text-lg font-black text-blue-700">{telemetry.conversionRatePct}%</span>
        </div>
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs">
          <span className="text-slate-500 text-[11px] block font-medium">Fraud Attempts Blocked</span>
          <span className="text-lg font-black text-rose-700">{telemetry.fraudBlockedAttemptsCount}</span>
        </div>
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs">
          <span className="text-slate-500 text-[11px] block font-medium">Cash Liability</span>
          <span className="text-lg font-black text-emerald-700">$0.00 (Zero)</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Referral Link & Shareable Tool Template Generator */}
        <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-xs space-y-4">
          <div className="flex items-center gap-2">
            <Share2 className="w-4 h-4 text-blue-900" />
            <h3 className="text-sm font-bold text-slate-900">Shareable Tool Link Generator</h3>
          </div>
          <p className="text-xs text-slate-600">
            Generates shareable links that invite friends to use YamooPay tools. Always shares tool templates — never private personal calculation outputs.
          </p>

          <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 space-y-2">
            <label className="text-[11px] font-bold text-slate-700 block">Sample User Referral Link:</label>
            <div className="flex items-center gap-2">
              <input
                type="text"
                readOnly
                value={sampleRefLink}
                className="w-full text-xs font-mono bg-white border border-slate-300 rounded px-2.5 py-1.5 text-slate-800"
              />
              <button
                onClick={copyLinkToClipboard}
                className="px-3 py-1.5 bg-blue-900 text-white rounded text-xs font-bold hover:bg-blue-800 transition-colors flex items-center gap-1 cursor-pointer shrink-0"
              >
                <Copy className="w-3.5 h-3.5" />
                <span>{copiedLink ? 'Copied!' : 'Copy Link'}</span>
              </button>
            </div>
          </div>

          <div className="p-3 bg-cyan-50 border border-cyan-200 rounded-lg text-xs text-cyan-950 space-y-1">
            <span className="font-bold block">Qualification Requirement:</span>
            <span>A referral is qualified ONLY when the referred friend completes their first useful financial calculation. Pageviews alone do NOT trigger rewards.</span>
          </div>
        </div>

        {/* Anti-Fraud & Risk Engine */}
        <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-xs space-y-4">
          <div className="flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 text-rose-700" />
            <h3 className="text-sm font-bold text-slate-900">Referral Anti-Abuse Risk Engine</h3>
          </div>

          <div className="space-y-2 text-xs">
            <div className="p-2.5 bg-slate-50 rounded-lg border border-slate-200 flex items-center justify-between">
              <span className="font-medium text-slate-700">Self-Referral & Same Device Detection</span>
              <span className="font-bold text-emerald-700 flex items-center gap-1">
                <CheckCircle2 className="w-3.5 h-3.5" />
                ACTIVE
              </span>
            </div>

            <div className="p-2.5 bg-slate-50 rounded-lg border border-slate-200 flex items-center justify-between">
              <span className="font-medium text-slate-700">Disposable Email Address Filtering</span>
              <span className="font-bold text-emerald-700 flex items-center gap-1">
                <CheckCircle2 className="w-3.5 h-3.5" />
                ACTIVE
              </span>
            </div>

            <div className="p-2.5 bg-slate-50 rounded-lg border border-slate-200 flex items-center justify-between">
              <span className="font-medium text-slate-700">Rapid Account Farming Rate Limiting</span>
              <span className="font-bold text-emerald-700 flex items-center gap-1">
                <CheckCircle2 className="w-3.5 h-3.5" />
                ACTIVE
              </span>
            </div>

            <div className="p-2.5 bg-slate-50 rounded-lg border border-slate-200 flex items-center justify-between">
              <span className="font-medium text-slate-700">No Address Book Scraping Policy</span>
              <span className="font-bold text-emerald-700 flex items-center gap-1">
                <CheckCircle2 className="w-3.5 h-3.5" />
                ENFORCED
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
